<script lang="ts">
import type { Component, Ref } from 'vue';
import type { CompositePrimaryKey, Filter, GlobalCollectionId, GlobalFieldId, LogicalFilterAND, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { ListingLayoutOption } from './types';

export interface ListingProps {
	showSelectionActions?: boolean;
	canBulkDelete?: boolean;
	canFilter?: boolean;
	canEditDisplayOptions?: boolean;
	collectionId: GlobalCollectionId;
	description?: string;
	title?: string;
	deleteLabels?: {
		button?: string;
		dialog?: {
			title?: string;
			description?: string;
			confirm?: string;
		};
		success: {
			title?: string;
		};
	};
	layoutProps?: Record<string, unknown>;
	viewId?: string;
	displayHasChanges?: boolean;
	sharedDisplayOptions?: Record<string, unknown>;
	layoutDisplayName?: string;
	layoutOptions: ListingLayoutOption[];
}

interface MergedLayoutOptions extends Record<string, unknown> {
	collection: ResolvedMonospaceCollection;
	displayOptions?: Record<string, unknown>;
}
</script>

<script lang="ts" setup>
import { whenever } from '@vueuse/core';
import { computed, markRaw, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '#imports';
import { isNullishOrEmpty } from '#layers/base/public';
import { isEmptyFilter, toApplicableFilter, useCollection } from '#layers/data-model/public';
import { FilterAdd, FilterBar, useFilterConfigResolver, useLogicalFilter } from '#layers/filters/public';
import { showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';
import { provideListingContext } from './context';
import ListingDisplayOptions from './ListingDisplayOptions.vue';
import ListingSelectionActions from './ListingSelectionActions.vue';
import ListingSearchInput from './SearchInput.vue';

const props = withDefaults(defineProps<ListingProps>(), {
	canFilter: true,
});

defineEmits<{
	saveForEveryone: [];
	resetView: [];
}>();

const slots = defineSlots<{
	'views'?: () => any;
	'title'?: () => any;
	'description'?: () => any;
	'default': (props: { filter: Filter; searchTerm: string; layoutProps: MergedLayoutOptions }) => any;
	'actions'?: () => any;
	'filter-actions'?: () => any;
}>();

const searchTerm = defineModel<string>('searchTerm', { default: '' });

const displayOptions = defineModel<Record<string, unknown> | undefined>('displayOptions', { default: () => ({}) });
const displayOptionsOpen = defineModel<boolean>('displayOptionsOpen', { default: false });
const layoutId = defineModel<string>('layoutId', { default: '$builtin:LayoutTable' });
const viewName = defineModel<string>('viewName');
const viewIcon = defineModel<string>('viewIcon');

const selection = defineModel<CompositePrimaryKey[]>('selection');

const filter = defineModel<Filter>('filter', { default: () => ({ _and: [] }) });

const { t } = useI18n();
const { findPrimitiveFieldById, findSingleRelationFieldById } = useWorkspaceSchemaStore();
const { handleBulkDelete } = useBulkActionHandlers();

const { collection } = useCollection(() => props.collectionId);

const filterViewActive = ref(false);
const hasActiveFilters = computed(() => !isEmptyFilter(toApplicableFilter(filter.value)));

const LayoutOptionComponent = shallowRef<Component | null>(null);

const mergedLayoutOptions = computed<MergedLayoutOptions>(() => ({
	...props.layoutProps,
	collection: collection.value!,
	displayOptions: displayOptions.value,
}));

const DisplayOptionsComponent = ref<Component | null>(null);

const showHeader = computed(() => props.title || props.description || props.canFilter || !!slots.actions || !!slots.views || LayoutOptionComponent.value);

const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();

const { handleAdd, handleGroupAdd } = useLogicalFilter(filter as Ref<LogicalFilterAND>, {
	findPrimitiveFieldById,
	findSingleRelationFieldById,
	supportedOperatorsFor,
	defaultOperatorFor,
});

const autoAdvanceIndex = ref<number | null>(null);

function addFieldFilter(fieldPath: GlobalFieldId[]) {
	const index = handleAdd(fieldPath);
	if (index === undefined) return;
	autoAdvanceIndex.value = index;
	filterViewActive.value = true; // the hasActiveFilters watcher won't fire — the clause is incomplete
}

function addFilterGroup() {
	handleGroupAdd();
	filterViewActive.value = true; // an empty group is pruned too, so the watcher won't fire either
}

provideListingContext({
	setDisplayOptionsComponent: (component: Component | null) => {
		DisplayOptionsComponent.value = component ? markRaw(component) : null;
	},
	...(props.canFilter ? { addFieldFilter } : {}),
});

whenever(hasActiveFilters, () => filterViewActive.value = true, { immediate: true });

function useBulkActionHandlers() {
	const store = useStore();
	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		error: {
			title: t('listing.selection.bulk_delete.error'),
		},
	});

	return { handleBulkDelete };

	async function handleBulkDelete() {
		if (isNullishOrEmpty(selection.value)) return;

		const collectionApi = store.$collection(collection.value!.apiName);
		const selectedCount = selection.value.length;

		const confirmed = await showConfirmationDialog({
			props: {
				title: props.deleteLabels?.dialog?.title ?? t('listing.selection.bulk_delete.confirm_title', { n: selectedCount }),
				description: props.deleteLabels?.dialog?.description ?? `${t('listing.selection.bulk_delete.confirm_description_delete_items', { n: selectedCount })}\n${t('listing.selection.bulk_delete.confirm_description_destructive')}`,
				confirm: { color: 'error', label: props.deleteLabels?.dialog?.confirm ?? t('listing.selection.bulk_delete.confirm_button') },
			},
		});

		if (confirmed) {
			try {
				await collectionApi.deleteMany(selection.value.map((item) => collectionApi.getKey(item)!));

				deleteSuccess({
					title: props.deleteLabels?.success.title ?? t('listing.selection.bulk_delete.success', { n: selectedCount }),
				});

				selection.value = [];
			}
			catch (error) {
				deleteError(error);
			}
		}
	}
}
</script>

<template>
	<!-- TODO error state in case of a missing collection -->
	<div class="flex h-full flex-1 flex-col overflow-hidden" data-listing>
		<div class="relative">
			<DSecondaryHeader
				v-if="showHeader"
				:title
				:description
				:icon="collection?.meta?.icon ?? undefined"
				:icon-color="collection?.meta?.color ?? undefined"
				bar
			>
				<template v-if="!!slots.title" #title>
					<slot name="title" />
				</template>
				<template v-if="!!slots.description" #description>
					<slot name="description" />
				</template>
				<template v-if="!!slots.views" #title-trailing>
					<slot name="views" />
				</template>
				<template #actions>
					<ListingSearchInput v-model="searchTerm" />

					<template v-if="canFilter && collection">
						<FilterAdd
							v-if="!hasActiveFilters && !filterViewActive"
							:collection
							@add-filter="addFieldFilter"
							@add-group="addFilterGroup"
						>
							<template #default="{ availableFilters }">
								<DTooltip :text="$t('filters.filter')">
									<DButton
										variant="outline"
										size="sm"
										color="neutral"
										:pressed="filterViewActive"
										icon="lucide:list-filter"
										:aria-label="$t('filters.filter')"
										:disabled="availableFilters.length === 0"
										data-testid="listing-filter-button"
									/>
								</DTooltip>
							</template>
						</FilterAdd>
						<DTooltip v-else :text="$t('filters.filter')">
							<DButton
								variant="outline"
								size="sm"
								color="neutral"
								:pressed="filterViewActive"
								:aria-label="$t('filters.filter')"
								data-testid="listing-filter-button"
								@click="filterViewActive = !filterViewActive"
							>
								<template #leading>
									<DChip
										:show="hasActiveFilters"
										inset
										size="sm"
										:ui="{ base: '-me-1 -mt-1 ring-(--ui-bg-elevated)' }"
									>
										<DIcon name="lucide:list-filter" size="16" />
									</DChip>
								</template>
							</DButton>
						</DTooltip>
					</template>

					<ListingDisplayOptions
						v-if="canEditDisplayOptions"
						v-model:layout-id="layoutId"
						v-model:name="viewName"
						v-model:icon="viewIcon"
						v-model:open="displayOptionsOpen"
						:has-changes="displayHasChanges"
						:name-placeholder="layoutDisplayName"
						:layout-options
						@save-for-everyone="$emit('saveForEveryone')"
						@reset="$emit('resetView')"
					>
						<DisplayOptionsComponent
							v-if="DisplayOptionsComponent"
							:key="viewId"
							v-bind="mergedLayoutOptions"
							:shared-display-options="props.sharedDisplayOptions"
							@update:display-options="displayOptions = $event"
						/>
					</ListingDisplayOptions>
					<slot name="actions" />
				</template>
			</DSecondaryHeader>

			<ListingSelectionActions
				:visible="showSelectionActions"
				:selection="selection ?? []"
				:can-bulk-delete
				:delete-label="deleteLabels?.button"
				@clear="selection = []"
				@delete-items="handleBulkDelete"
			/>
		</div>
		<Transition
			enter-from-class="h-0! opacity-0"
			enter-to-class="opacity-100"
			enter-active-class="transition-all motion-reduce:transition-none duration-very-fast"
			leave-from-class="opacity-100"
			leave-to-class="h-0! opacity-0"
			leave-active-class="transition-all motion-reduce:transition-none duration-very-fast"
		>
			<div
				v-if="canFilter && filterViewActive && collection"
				class="flex h-11 shrink-0 items-center justify-between gap-2 overflow-hidden border-b px-6"
				data-listing-header
			>
				<FilterBar
					v-model="filter as LogicalFilterAND"
					:collection
					:auto-advance-index
					@auto-advance-handled="autoAdvanceIndex = null"
				/>
				<slot name="filter-actions" />
			</div>
		</Transition>
		<slot
			:filter
			:search-term
			:layout-props="mergedLayoutOptions"
		/>
	</div>
</template>
